import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';

type ToastProps = {
  message: string;
  type: 'success' | 'error';
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
};

export const Toast: React.FC<ToastProps> = ({
  message,
  type,
  isVisible,
  onClose,
  duration = 4000
}) => {
  const [progress, setProgress] = useState(100);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      setProgress(100);
      
      const interval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev - (100 / (duration / 50));
          if (newProgress <= 0) {
            clearInterval(interval);
            setIsAnimating(false);
            setTimeout(() => {
              onClose();
            }, 300); // Wait for fade out animation
            return 0;
          }
          return newProgress;
        });
      }, 50);

      return () => clearInterval(interval);
    } else {
      setIsAnimating(false);
      setProgress(100);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible && !isAnimating) return null;

  const successStyles = {
    bg: 'bg-gradient-to-r from-green-500 to-green-600',
    iconBg: 'bg-green-100',
    icon: 'text-green-600',
    text: 'text-white',
    border: 'border-green-400',
    shadow: 'shadow-green-500/50'
  };

  const errorStyles = {
    bg: 'bg-gradient-to-r from-red-500 to-red-600',
    iconBg: 'bg-red-100',
    icon: 'text-red-600',
    text: 'text-white',
    border: 'border-red-400',
    shadow: 'shadow-red-500/50'
  };

  const styles = type === 'success' ? successStyles : errorStyles;

  return (
    <div 
      className={`fixed top-4 right-4 z-50 transition-all duration-300 ease-in-out ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
      }`}
      style={{ maxWidth: '28rem', width: 'calc(100vw - 2rem)' }}
    >
      <div
        className={`${styles.bg} ${styles.border} border-2 rounded-xl shadow-2xl ${styles.shadow} pointer-events-auto overflow-hidden backdrop-blur-sm`}
      >
        {/* Progress bar */}
        <div 
          className="h-1 bg-white/30 transition-all duration-50 ease-linear"
          style={{ width: `${progress}%` }}
        />
        
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className={`flex-shrink-0 ${styles.iconBg} rounded-full p-1.5`}>
              {type === 'success' ? (
                <CheckCircleIcon className={`h-5 w-5 ${styles.icon}`} />
              ) : (
                <XCircleIcon className={`h-5 w-5 ${styles.icon}`} />
              )}
            </div>
            
            {/* Message */}
            <div className="flex-1 min-w-0 pt-0.5">
              <p className={`${styles.text} text-sm font-semibold leading-relaxed`}>
                {message}
              </p>
            </div>
            
            {/* Close button */}
            <div className="flex-shrink-0">
              <button
                className={`${styles.text} hover:bg-white/20 rounded-lg p-1 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white/50`}
                onClick={() => {
                  setIsAnimating(false);
                  setTimeout(() => onClose(), 300);
                }}
                aria-label="Đóng thông báo"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

